import { css } from '@emotion/css';
import {
  ActionIcon,
  Box,
  Group,
  NumberInput,
  Select,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useIsFirstRender } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { AnimatePresence, motion } from 'motion/react';
import type { JSX } from 'react';
import { isNullish } from 'remeda';

import { useAutomationWizardController } from '../controller';
import type { AutomationFieldType } from '../controller/field-types';

import { renderFieldItem } from './ActionCard/components';

type ConditionProps = {
  conditionIndex: number;
};

export const ConditionCard = ({ conditionIndex }: ConditionProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    form,
    conditions: {
      conditionsCount,
      removeCondition,
      selectConditionField,
      getFieldFieldProps,
      getOperatorFieldProps,
      getOperandFieldProps,
      isOperandFieldVisible,
      getConditionFieldType,
    },
  } = useAutomationWizardController();
  const isFirstRender = useIsFirstRender();

  const operandInputProps = {
    autoFocus: true,
    ...form.getInputProps(`conditions.${conditionIndex}.operand`),
    ...getOperandFieldProps(conditionIndex),
    label: t(i18nKeys.AUTOMATION.CONDITIONS.VALUE_LABEL),
  };

  const typeToInputField: Record<AutomationFieldType, JSX.Element> = {
    string: (
      <TextInput
        placeholder={t(i18nKeys.AUTOMATION.CONDITIONS.VALUE_LABEL)}
        {...operandInputProps}
      />
    ),
    select: (
      <Select
        placeholder={t(i18nKeys.AUTOMATION.CONDITIONS.VALUE_PLACEHOLDER)}
        {...operandInputProps}
      />
    ),
    boolean: (
      <Select
        placeholder={t(i18nKeys.AUTOMATION.CONDITIONS.VALUE_PLACEHOLDER)}
        {...operandInputProps}
      />
    ),
    number: <NumberInput hideControls placeholder="10 000" {...operandInputProps} />,
    date: <DatePickerInput {...operandInputProps} />,
    id: (
      <TextInput
        placeholder={t(i18nKeys.AUTOMATION.CONDITIONS.VALUE_LABEL)}
        {...operandInputProps}
      />
    ),
    array: (
      <TextInput
        placeholder={t(i18nKeys.AUTOMATION.CONDITIONS.VALUE_LABEL)}
        {...operandInputProps}
      />
    ),
  };

  return (
    <Group
      bg={theme.colors.purple[1]}
      mb="md"
      bdrs={8}
      p="lg"
      pt="sm"
      gap="md"
      w="100%"
      style={{ position: 'relative' }}
    >
      {conditionsCount !== 1 && (
        <Box style={{ position: 'absolute', top: 6, right: 6 }}>
          <ActionIcon
            aria-label={t(i18nKeys.AUTOMATION.CONDITIONS.REMOVE_CONDITION)}
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => removeCondition(conditionIndex)}
          >
            <IconX size={16} color="var(--mantine-color-gray-6)" />
          </ActionIcon>
        </Box>
      )}
      <Select
        width={200}
        searchable
        label={t(i18nKeys.AUTOMATION.CONDITIONS.FIELD_LABEL)}
        placeholder={t(i18nKeys.AUTOMATION.CONDITIONS.FIELD_PLACEHOLDER)}
        renderOption={renderFieldItem}
        allowDeselect={false}
        classNames={{
          option: selectedFieldStyle,
        }}
        {...form.getInputProps(`conditions.${conditionIndex}.fieldName`)}
        {...getFieldFieldProps(conditionIndex)}
        onChange={v => {
          if (isNullish(v)) return;
          selectConditionField(conditionIndex, v);
        }}
      />
      <Select
        width={200}
        label={t(i18nKeys.AUTOMATION.CONDITIONS.OPERATOR_LABEL)}
        renderOption={renderFieldItem}
        allowDeselect={false}
        classNames={{
          option: selectedFieldStyle,
        }}
        {...form.getInputProps(`conditions.${conditionIndex}.operator`)}
        {...getOperatorFieldProps(conditionIndex)}
      />
      <AnimatePresence>
        {isOperandFieldVisible(conditionIndex) && (
          <motion.div {...(isFirstRender ? {} : slideFromLeftAnimationProps)}>
            {typeToInputField[getConditionFieldType(conditionIndex)!]}
          </motion.div>
        )}
      </AnimatePresence>
    </Group>
  );
};

const selectedFieldStyle = css`
  &:not(:last-of-type) {
    margin-bottom: 4px;
  }
  &[data-checked='true'] {
    background-color: var(--mantine-color-purple-1);
  }
`;

const slideFromLeftAnimationProps = {
  initial: { opacity: 0, width: 0, maxHeight: 60 },
  animate: { opacity: 1, width: 200 },
  exit: { opacity: 0, width: 0 },
  transition: { duration: 0.2 },
};
